<template>
  <div id="quill-wrapper">
    <link href="https://cdn.jsdelivr.net/npm/quill@2.0.3/dist/quill.snow.css" rel="stylesheet" />
    <label
      :for="`${name}-editor`"
      :class="['mt-4 block text-sm font-medium leading-5 text-gray-700 mb-1', { 'sr-only': labelSrOnly }]"
      >{{ label }} <span v-if="optional" class="text-xs text-gray-700">(optional)</span></label
    >
    <div
      :id="`${name}-editor`"
      :ref="el => (editor = el)"
      :class="`user-generated-content quill-editor-container rounded-br-lg rounded-bl-lg ${size}`"
    ></div>
    <Modal
      v-if="showAiModal"
      :ref="el => (modal = el)"
      title="Generate AI Content"
      action="Generate"
      @close-modal="showAiModal = false"
      @handle-action="generateAiContent"
    >
      <TextAreaInput class="mt-4" label="Prompt" :model-value="aiPrompt" @update:model-value="aiPrompt = $event" />
    </Modal>
  </div>
</template>

<script setup>
import { ref, onMounted, inject, watch, defineExpose } from 'vue';
import Quill from 'quill';
import Modal from './Modal.vue';
import TextAreaInput from './TextAreaInput.vue';
import { createAiContent } from '../../api/ai_content_generation';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';
import { mergeTagPlaceholder } from './merge_tag/MergeTagSelect/mergeTagPlaceholder';
import { MergeTagBlot } from './merge_tag/quill_input/MergeTagBlot';

if (!Quill.imports['formats/mergeTag']) {
  Quill.register(MergeTagBlot);
}

const store = inject('store', null);
const loadingSpinnerStore = useLoadingSpinnerStore();

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  modelValue: {
    type: String,
    required: true,
  },
  optional: {
    type: Boolean,
    required: false,
    default: false,
  },
  labelSrOnly: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'medium',
  },
});

const emit = defineEmits(['update:modelValue', 'blur']);

const editor = ref(null);
const modal = ref(null);
let quillInstance = null;
const showAiModal = ref(false);
const aiPrompt = ref('');

watch(
  () => props.modelValue,
  newValue => {
    if (quillInstance && newValue !== quillInstance.root.innerHTML) {
      const delta = quillInstance.clipboard.convert({ html: newValue });
      quillInstance.setContents(delta, 'silent');
    }
  },
  { deep: true }
);

onMounted(() => {
  if (editor.value) {
    quillInstance = new Quill(editor.value, {
      theme: 'snow',
      bounds: '#quill-wrapper',
    });

    if (props.modelValue) {
      const value = props.modelValue;
      const delta = quillInstance.clipboard.convert({ html: value });
      quillInstance.setContents(delta, 'silent');
    }

    addCustomQuillButton({
      label:
        '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" color="currentColor"><path d="m21.255 7.134l-.494-.857c-.373-.648-.56-.972-.877-1.1c-.71-.29-1.908.32-2.615.52c-.627.144-1.165-.058-1.696-.364a2 2 0 0 1-.788-.968c-.214-.64-.358-1.767-.925-2.176C13.6 2 13.252 2 12.557 2h-1.114c-.695 0-1.042 0-1.303.189c-.567.409-.71 1.536-.925 2.176c-.213.554-.633.878-1.125 1.162a2 2 0 0 1-1.359.17c-.706-.2-1.905-.81-2.615-.52c-.317.128-.504.452-.877 1.1l-.494.857c-.35.607-.525.91-.49 1.234c.083.803 1.253 1.682 1.767 2.257c.252.319.43.875.43 1.375s-.178 1.056-.43 1.375c-.514.575-1.684 1.454-1.768 2.257c-.034.324.141.627.491 1.234l.494.857c.373.648.56.972.877 1.1c.71.29 1.909-.32 2.615-.52c.982-.226 2.123.39 2.484 1.332c.214.64.358 1.767.925 2.176c.261.189.608.189 1.303.189h1.114c.695 0 1.042 0 1.303-.189c.262-.189.372-.518.591-1.178c.247-.74.42-1.517 1.122-1.966c.53-.306 1.069-.508 1.696-.364c.707.2 1.905.81 2.615.52c.317-.128.504-.452.877-1.1l.494-.857c.35-.607.525-.91.49-1.234c-.084-.803-1.253-1.682-1.767-2.257c-.583-.738-.582-2.012 0-2.75c.514-.575 1.683-1.454 1.768-2.257c.034-.324-.141-.627-.491-1.234"/><path d="m7.438 14.992l1.841-5.525a.694.694 0 0 1 1.317 0l1.842 5.525m-4-2h3m4-4v6"/></g></svg>',
      onClick: () => {
        showAiModal.value = true;
      },
    });

    quillInstance.on('text-change', () => {
      emit('update:modelValue', quillInstance.root.innerHTML);
    });

    quillInstance.on('selection-change', (range, oldRange, source) => {
      if (!range && oldRange) {
        emit('blur');
      }
    });
  }
});

function addCustomQuillButton({ label, onClick }) {
  const toolbar = quillInstance.getModule('toolbar').container;

  const button = document.createElement('button');
  button.classList.add('ql-custom-button');
  button.innerHTML = label;

  const span = document.createElement('span');
  span.classList.add('ql-formats');

  span.appendChild(button);
  toolbar.appendChild(span);

  button.addEventListener('click', e => {
    e.preventDefault();
    e.stopPropagation();
    onClick();
  });
}

function insertTextAtCursor(text) {
  if (text === mergeTagPlaceholder) return;
  if (quillInstance) {
    let position = quillInstance.getSelection(true);
    quillInstance.insertText(position, text);
  }
}

function insertMergeTagAtCursor(mergePretty) {
  if (quillInstance) {
    let range = quillInstance.getSelection(true);
    quillInstance.insertEmbed(range.index, 'mergeTag', mergePretty);
    quillInstance.setSelection(range.index + 1, 0);
  }
}

async function generateAiContent() {
  const response = await createAiContent({ task: aiPrompt.value });

  if (response.status === 'success') {
    if (modal.value && typeof modal.value.success === 'function') {
      modal.value.success();
    } else if (store) {
      loadingSpinnerStore.hideLocalLoadingSpinner();
    }

    insertTextAtCursor(response.content);
    aiPrompt.value = '';
  } else {
    if (modal.value && typeof modal.value.error === 'function') {
      modal.value.error();
    } else if (store) {
      loadingSpinnerStore.hideLocalLoadingSpinner();
    }
  }

  showAiModal.value = false;
}

defineExpose({
  insertTextAtCursor,
  insertMergeTagAtCursor,
  setFocus: () => {
    quillInstance?.focus();
  },
});
</script>

<style>
@reference "tailwindcss";
.quill-editor-container.ql-container.ql-snow {
  @apply h-48;
}

#quill-wrapper .large {
  @apply h-64;
}

#quill-wrapper .x-large {
  @apply h-[40rem];
}

#quill-wrapper .ql-toolbar.ql-snow {
  @apply border-slate-300 rounded-tl-lg rounded-tr-lg;
}

#quill-wrapper .ql-container.ql-snow {
  @apply border-slate-300;
}
#quill-wrapper .ql-picker-label svg {
  width: 18px;
  height: 18px;
}

#quill-wrapper .ql-custom-button {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
